import { createFeatureSelector, createSelector } from '@ngrx/store';
import { selectActiveConfigVersion, selectPlantCCDCs, selectPlants, selectTechnologies } from 'src/app/masterdata/state/master-data.selectors';
import { selectCurrentUserQNumber, selectIsSupplier, selectUserLanguage, selectUserPermissions, selectUserPrefs } from '../../core/state/core.selectors';
import { TranslationUtils } from '../../shared/utils/translation-utils';
import { AnalysisStatus } from '../models/AnalysisStatus';
import { AnalysisSupplierStatus } from '../models/AnalysisSupplierStatus';
import { EditMode } from '../models/EditMode';
import { HeaderDataInfo } from '../models/HeaderData';
import { Criterion } from '../models/criterions/Criterion';
import { CriterionData } from '../models/criterions/CriterionData';
import { AnalysisUtils } from '../utils/analysis-utils';
import { AnalysisState, featureKey } from './analysis.state';
import { AnalysisType } from '../models/AnalysisType';
import { AnalysisPage } from '../models/AnalysisPage';
import { SelectListOption } from '@bmw-ds/components';
import { Analysis } from 'src/app/analysis/models/Analysis';
import { GDMSUtils } from '../header-data/header-optional-data/header-gdms-popup/gdms-utils.component';
import { AnalysisForm } from 'src/app/analysis/models/AnalysisForm';
import { DateService } from '../../shared/services/date-service';

export const selectAnalysisState = createFeatureSelector<AnalysisState>(featureKey);

export const selectAnalysis = createSelector(selectAnalysisState, state => state?.analysis);

export const selectLoadingAnalysis = createSelector(selectAnalysisState, state => state?.loadingAnalysis);

export const selectLoadingPrintAnalysis = createSelector(selectAnalysisState, state => state?.loadingPrintAnalysis);

export const selectSelectedAnalysisPage = createSelector(selectAnalysisState, state => state?.selectedAnalysisPage);

export const selectAnalysisId = createSelector(selectAnalysis, state => state?.id);

export const selectWorkplaceNumber = createSelector(selectAnalysis, state => state?.workPlaceNumber);

export const selectAnalysisPlantCode = createSelector(selectAnalysis, state => state?.plantCode);

export const selectAnalysisPlantCCDCsDropdownOptions = createSelector(selectPlantCCDCs, selectAnalysis, (plantCCDCs, analysis) => {
    const combined = [...(plantCCDCs ?? []), ...(analysis?.plantCCDC ?? [])];
    return combined
        .filter(plantCCDC => plantCCDC?.id != null)
        .map(plantCCDC => {
            return {
                id: plantCCDC.id.toString(),
                label: plantCCDC.displayName
            } as SelectListOption;
        });
});

export const selectIsFavorite = createSelector(selectAnalysis, state => state?.isFavorite ?? false);

export const selectMasterDataPlant = createSelector(selectAnalysisPlantCode, selectPlants, (analysisPlant, plants) =>
    plants?.find(plant => plant.plantCode === analysisPlant)
);

export const selectAnalysisTechnology = createSelector(selectAnalysis, state => state?.technologyCode);
export const selectMasterDataTechnology = createSelector(selectAnalysisTechnology, selectTechnologies, (analysisTechnology, technologies) =>
    technologies?.find(technology => technology.technologyCode === analysisTechnology)
);
export const selectPlantCCDC = createSelector(selectAnalysis, state => state?.plantCCDC);
export const selectTypeOfAnalysis = createSelector(selectAnalysis, state => state?.analysisType);
export const selectIsTMO = createSelector(selectAnalysisTechnology, technologyCode => technologyCode === 'TMO');
export const selectIsTMOTKB = createSelector(selectAnalysisTechnology, technologyCode => technologyCode === 'TMO' || technologyCode === 'TKB');
export const selectIsTVG = createSelector(selectTypeOfAnalysis, analysisType => analysisType === ('TVG' as AnalysisType));
export const selectShowFurtherCreator = createSelector(
    selectTypeOfAnalysis,
    analysisType =>
        analysisType === ('FREE' as AnalysisType) ||
        analysisType === ('FREE_SUPPORT' as AnalysisType) ||
        analysisType === ('SUPPORT' as AnalysisType) ||
        analysisType === ('AVERAGE' as AnalysisType) ||
        analysisType === ('ADDITIONAL' as AnalysisType)
);
export const selectDescription = createSelector(selectAnalysis, selectUserLanguage, selectIsTVG, (analysis, userLanguage, isTVG) =>
    isTVG ? TranslationUtils.getI18nText(analysis?.i18nTextList, 'TVG_TITLE', userLanguage, analysis?.description) : analysis?.description
);
export const selectIsNotGroupAnalysis = createSelector(selectAnalysis, analysisState => {
    const analysisType = analysisState?.analysisType;
    return analysisType ? !AnalysisUtils.isGroupAnalysis(analysisType) : false;
});
export const selectFormOfAnalysis = createSelector(selectAnalysis, state => state?.analysisForm);
export const selectStatus = createSelector(selectAnalysis, state => state?.status);
export const selectSupplierStatus = createSelector(selectAnalysis, state => state?.supplierStatus);
export const selectTaskLength = createSelector(selectAnalysis, state => state?.taskLength);
export const selectProcessFrequency = createSelector(selectAnalysis, state => state?.processFrequency);
export const selectTimeUnitSecond = createSelector(selectAnalysis, state => state?.timeUnitSecond);
export const selectNumberIdenticalWorkplace = createSelector(selectAnalysis, state => state?.numberIdenticalWorkPlace);
export const selectValidFrom = createSelector(selectAnalysis, state => state?.validFrom);
export const selectValidTo = createSelector(selectAnalysis, state => state?.validTo);
export const selectCostCenter = createSelector(selectPlantCCDC, state => state?.map(ccdc => ccdc.costCenterCode));
export const selectUniqueCostCenters = createSelector(selectCostCenter, costCenters => {
    return [...new Set(costCenters || [])];
});

export const selectAttachments = createSelector(selectAnalysis, state => state?.attachments);
export const selectProblems = createSelector(selectAnalysis, state => state?.problems);
export const selectAnalysisShiValue = createSelector(selectAnalysis, state => state?.shiValue);
export const selectAnalysisShiColor = createSelector(selectAnalysis, state => state?.color);
export const selectAnalysisBodyScore = createSelector(selectAnalysis, state => state?.bodyScore);
export const selectAnalysisBodyScoreColor = createSelector(selectAnalysis, state => state?.bodyScoreColor);

export const selectAnalysisMM01 = createSelector(selectAnalysis, state => state?.dataMM01);

export const selectAnalysisMM01Color = createSelector(selectAnalysisMM01, state => state?.color);

export const selectAnalysisMM02 = createSelector(selectAnalysis, state => state?.dataMM02);

export const selectAnalysisMM02Color = createSelector(selectAnalysisMM02, state => state?.color);

export const selectAnalysisMM03 = createSelector(selectAnalysis, state => state?.dataMM03);

export const selectAnalysisMM03Color = createSelector(selectAnalysisMM03, state => state?.color);

export const selectAnalysisMM04 = createSelector(selectAnalysis, state => state?.dataMM04);

export const selectAnalysisMM04Color = createSelector(selectAnalysisMM04, state => state?.color);

export const selectAnalysisMM05 = createSelector(selectAnalysis, state => state?.dataMM05);

export const selectAnalysisMM05Color = createSelector(selectAnalysisMM05, state => state?.color);

export const selectAnalysisMM06 = createSelector(selectAnalysis, state => state?.dataMM06);

export const selectAnalysisMM06Color = createSelector(selectAnalysisMM06, state => state?.color);

export const selectAnalysisMM07 = createSelector(selectAnalysis, state => state?.dataMM07);

export const selectAnalysisMM07Color = createSelector(selectAnalysisMM07, state => state?.color);

export const selectAnalysisMM08 = createSelector(selectAnalysis, state => state?.dataMM08);

export const selectAnalysisMM08Color = createSelector(selectAnalysisMM08, state => state?.color);

export const selectAnalysisMM09 = createSelector(selectAnalysis, state => state?.dataMM09);

export const selectAnalysisMM09Color = createSelector(selectAnalysisMM09, state => state?.color);

export const selectAnalysisMM10 = createSelector(selectAnalysis, state => state?.dataMM10);

export const selectAnalysisMM10Color = createSelector(selectAnalysisMM10, state => state?.color);

export const selectAnalysisMM11 = createSelector(selectAnalysis, state => state?.dataMM11);

export const selectAnalysisMM11Color = createSelector(selectAnalysisMM11, state => state?.color);

export const selectAnalysisMM12 = createSelector(selectAnalysis, state => state?.dataMM12);

export const selectAnalysisMM12Color = createSelector(selectAnalysisMM12, state => state?.color);

export const selectAnalysisMM13 = createSelector(selectAnalysis, state => state?.dataMM13);

export const selectAnalysisMM13Color = createSelector(selectAnalysisMM13, state => state?.color);

const selectCriterionDataSection1 = createSelector(
    selectAnalysisMM01,
    selectAnalysisMM02,
    selectAnalysisMM03,
    selectAnalysisMM04,
    selectAnalysisMM05,
    selectAnalysisMM06,
    selectAnalysisMM07,
    (MM01, MM02, MM03, MM04, MM05, MM06, MM07) =>
        [
            { criterion: Criterion.MM01, trafficLightColor: MM01?.color, label: 'MM01', noStress: MM01?.noStress, shiValue: MM01?.shiValue },
            { criterion: Criterion.MM02, trafficLightColor: MM02?.color, label: 'MM02', noStress: MM02?.noStress, shiValue: MM02?.shiValue },
            { criterion: Criterion.MM03, trafficLightColor: MM03?.color, label: 'MM03', noStress: MM03?.noStress, shiValue: MM03?.shiValue },
            { criterion: Criterion.MM04, trafficLightColor: MM04?.color, label: 'MM04', noStress: MM04?.noStress, shiValue: MM04?.shiValue },
            { criterion: Criterion.MM05, trafficLightColor: MM05?.color, label: 'MM05', noStress: MM05?.noStress, shiValue: MM05?.shiValue },
            { criterion: Criterion.MM06, trafficLightColor: MM06?.color, label: 'MM06', shiValue: MM06?.shiValue },
            { criterion: Criterion.MM07, trafficLightColor: MM07?.color, label: 'MM07', noStress: MM07?.noStress, shiValue: MM07?.shiValue }
        ] as CriterionData[]
);

const selectCriterionDataSection2 = createSelector(
    selectAnalysisMM08,
    selectAnalysisMM09,
    selectAnalysisMM10,
    (MM08, MM09, MM10) =>
        [
            { criterion: Criterion.MM08, trafficLightColor: MM08?.color, label: 'MM08', shiValue: MM08?.shiValue },
            { criterion: Criterion.MM09, trafficLightColor: MM09?.color, label: 'MM09', shiValue: MM09?.shiValue },
            { criterion: Criterion.MM10, trafficLightColor: MM10?.color, label: 'MM10', shiValue: MM10?.shiValue }
        ] as CriterionData[]
);

const selectCriterionDataSection3 = createSelector(
    selectAnalysisMM11,
    selectAnalysisMM12,
    selectAnalysisMM13,
    (MM11, MM12, MM13) =>
        [
            { criterion: Criterion.MM11, trafficLightColor: MM11?.color, label: 'MM11', noStress: MM11?.noStress, shiValue: MM11?.shiValue },
            { criterion: Criterion.MM12, trafficLightColor: MM12?.color, label: 'MM12', shiValue: MM12?.shiValue },
            { criterion: Criterion.MM13, trafficLightColor: MM13?.color, label: 'MM13', noStress: MM13?.noStress, shiValue: MM13?.shiValue }
        ] as CriterionData[]
);

export { selectCriterionDataSection2, selectCriterionDataSection3 };

export const selectAnalysisCriterionData = createSelector(
    selectCriterionDataSection1,
    selectCriterionDataSection2,
    selectCriterionDataSection3,
    (section1, section2, section3) => {
        return [...section1, ...section2, ...section3];
    }
);

export const selectAnalysisValueSection1 = createSelector(selectAnalysis, state => state?.shiValueSection1);

export const selectAnalysisColorSection1 = createSelector(selectAnalysis, state => state?.colorSection1);

export const selectAnalysisValueSection2 = createSelector(selectAnalysis, state => state?.shiValueSection2);

export const selectAnalysisColorSection2 = createSelector(selectAnalysis, state => state?.colorSection2);

export const selectAnalysisValueSection3 = createSelector(selectAnalysis, state => state?.shiValueSection3);

export const selectAnalysisColorSection3 = createSelector(selectAnalysis, state => state?.colorSection3);

export const selectAnalysisSection1 = createSelector(selectAnalysisValueSection1, selectAnalysisColorSection1, (shiValue, color) => {
    return {
        shiValue: shiValue,
        color: color
    };
});

export const selectAnalysisSection2 = createSelector(selectAnalysisValueSection2, selectAnalysisColorSection2, (shiValue, color) => {
    return {
        shiValue: shiValue,
        color: color
    };
});

export const selectAnalysisSection3 = createSelector(selectAnalysisValueSection3, selectAnalysisColorSection3, (shiValue, color) => {
    return {
        shiValue: shiValue,
        color: color
    };
});

const selectMMDataSection1 = createSelector(
    selectAnalysisMM01,
    selectAnalysisMM02,
    selectAnalysisMM03,
    selectAnalysisMM04,
    selectAnalysisMM05,
    selectAnalysisMM06,
    selectAnalysisMM07,
    (MM01, MM02, MM03, MM04, MM05, MM06, MM07) => ({ MM01, MM02, MM03, MM04, MM05, MM06, MM07 })
);

const selectMMDataSection2 = createSelector(selectAnalysisMM08, selectAnalysisMM09, selectAnalysisMM10, (MM08, MM09, MM10) => ({ MM08, MM09, MM10 }));

const selectMMDataSection3 = createSelector(selectAnalysisMM11, selectAnalysisMM12, selectAnalysisMM13, (MM11, MM12, MM13) => ({ MM11, MM12, MM13 }));

const selectAllSections = createSelector(selectAnalysisSection1, selectAnalysisSection2, selectAnalysisSection3, (SEC1, SEC2, SEC3) => ({ SEC1, SEC2, SEC3 }));

export const selectAnalysisRiskData = createSelector(
    selectMMDataSection1,
    selectMMDataSection2,
    selectMMDataSection3,
    selectAllSections,
    (section1Data, section2Data, section3Data, sections) => {
        const { MM01, MM02, MM03, MM04, MM05, MM06, MM07 } = section1Data;
        const { MM08, MM09, MM10 } = section2Data;
        const { MM11, MM12, MM13 } = section3Data;
        const { SEC1, SEC2, SEC3 } = sections;
        return [
            {
                riskValue: SEC1.shiValue,
                riskColor: SEC1.color,
                items: [
                    {
                        riskValue: MM01?.shiValue,
                        riskColor: MM01?.color,
                        noStress: MM01?.noStress
                    },
                    {
                        riskValue: MM02?.shiValue,
                        riskColor: MM02?.color,
                        noStress: MM02?.noStress
                    },
                    {
                        riskValue: MM03?.shiValue,
                        riskColor: MM03?.color,
                        noStress: MM03?.noStress
                    },
                    {
                        riskValue: MM04?.shiValue,
                        riskColor: MM04?.color,
                        noStress: MM04?.noStress
                    },
                    {
                        riskValue: MM05?.shiValue,
                        riskColor: MM05?.color,
                        noStress: MM05?.noStress
                    },
                    {
                        riskValue: MM06?.shiValue,
                        riskColor: MM06?.color
                    },
                    {
                        riskValue: MM07?.shiValue,
                        riskColor: MM07?.color,
                        noStress: MM07?.noStress
                    }
                ]
            },
            {
                riskValue: SEC2.shiValue,
                riskColor: SEC2.color,
                items: [
                    {
                        riskValue: MM08?.shiValue,
                        riskColor: MM08?.color
                    },
                    {
                        riskValue: MM09?.shiValue,
                        riskColor: MM09?.color
                    },
                    {
                        riskValue: MM10?.shiValue,
                        riskColor: MM10?.color
                    }
                ]
            },
            {
                riskValue: SEC3.shiValue,
                riskColor: SEC3.color,
                items: [
                    {
                        riskValue: MM11?.shiValue,
                        riskColor: MM11?.color,
                        noStress: MM11?.noStress
                    },
                    {
                        riskValue: MM12?.shiValue,
                        riskColor: MM12?.color
                    }
                ]
            },
            {
                riskValue: MM13?.shiValue,
                riskColor: MM13?.color,
                items: [
                    {
                        riskValue: MM13?.shiValue,
                        riskColor: MM13?.color
                    }
                ]
            }
        ];
    }
);

export const selectPreviewAnalysisCriterionData = selectCriterionDataSection1;

export const selectAnalysisSupportCriterionBar = createSelector(
    selectAnalysisMM08Color,
    selectAnalysisMM09Color,
    selectAnalysisMM10Color,
    selectAnalysisMM11Color,
    selectAnalysisMM12Color,
    selectAnalysisMM13Color,
    (colorMM08, colorMM09, colorMM10, colorMM11, colorMM12, colorMM13) => {
        const criterionBarData: CriterionData[] = [
            { criterion: Criterion.MM08, trafficLightColor: colorMM08, label: 'MM08' },
            { criterion: Criterion.MM09, trafficLightColor: colorMM09, label: 'MM09' },
            { criterion: Criterion.MM10, trafficLightColor: colorMM10, label: 'MM10' },
            { criterion: Criterion.MM11, trafficLightColor: colorMM11, label: 'MM11' },
            { criterion: Criterion.MM12, trafficLightColor: colorMM12, label: 'MM12' },
            { criterion: Criterion.MM13, trafficLightColor: colorMM13, label: 'MM13' }
        ];
        return criterionBarData;
    }
);

export const selectAnalysisSupportMM13CriterionBar = createSelector(selectAnalysisMM13Color, colorMM13 => {
    const criterionBarData: CriterionData[] = [
        {
            criterion: Criterion.MM13,
            trafficLightColor: colorMM13,
            label: 'MM13'
        }
    ];
    return criterionBarData;
});

export const selectIsEditMode = createSelector(
    selectAnalysis,
    state =>
        !(state?.isPreview ?? false) &&
        (state?.editMode === EditMode.EDIT || state?.editMode === EditMode.ITO_SAME_VERSION || (state?.editMode === null && state?.isEditMode))
);

export const selectIsNotEditMode = createSelector(selectIsEditMode, state => (state ? !state : true));
export const selectEditMode = createSelector(selectAnalysis, state => state?.editMode);
export const selectVersion = createSelector(selectAnalysis, state => state?.version);
export const selectAnalysisKey = createSelector(selectAnalysis, state => state?.analysisKey);
export const selectCreatedBy = createSelector(selectAnalysis, state => state?.createdBy);
export const selectId = createSelector(selectAnalysis, state => state?.id);
export const selectMaxVersion = createSelector(selectAnalysis, state => state?.maxVersion);
export const selectHasHigherVersion = createSelector(
    selectVersion,
    selectMaxVersion,
    (version, maxVersion) => !!(maxVersion && version && maxVersion > version)
);

export const selectIsPreview = createSelector(selectAnalysis, state => state?.isPreview ?? false);

export const selectDWSMJobId = createSelector(selectAnalysis, state => state?.dwsmJobId);

const selectHeaderDataBasic = createSelector(
    selectAnalysisId,
    selectWorkplaceNumber,
    selectDescription,
    selectPlantCCDC,
    selectTaskLength,
    selectNumberIdenticalWorkplace,
    selectAnalysisTechnology,
    (id, workplaceNumber, description, plantCCDC, taskLength, numberIdenticalWorkplace, analysisTechnology) => ({
        id,
        workplaceNumber,
        description,
        plantCCDC,
        taskLength,
        numberIdenticalWorkplace,
        analysisTechnology
    })
);

const selectHeaderDataExtended = createSelector(
    selectValidFrom,
    selectFormOfAnalysis,
    selectMasterDataTechnology,
    selectAnalysisCriterionData,
    selectIsEditMode,
    selectEditMode,
    selectHasHigherVersion,
    (validFrom, analysisForm, masterDataTechnology, criterionsData, isEditMode, editMode, hasHigherVersion) => ({
        validFrom,
        analysisForm,
        masterDataTechnology,
        criterionsData,
        isEditMode,
        editMode,
        hasHigherVersion
    })
);

export { selectHeaderDataBasic, selectHeaderDataExtended };

export const selectHeaderDataInformation = createSelector(selectHeaderDataBasic, selectHeaderDataExtended, selectStatus, (basic, extended, status) => {
    const headerDataInfo: HeaderDataInfo = {
        id: basic.id,
        workPlaceNumber: basic.workplaceNumber,
        description: basic.description,
        plantCCDC: basic.plantCCDC ?? [],
        taskLength: basic.taskLength,
        numberIdenticalWorkPlace: basic.numberIdenticalWorkplace,
        technologyCode: basic.analysisTechnology,
        validFrom: extended.validFrom,
        analysisForm: extended.analysisForm,
        selectedMasterDataTechnology: extended.masterDataTechnology,
        criterionsData: extended.criterionsData,
        isEditMode: extended.isEditMode ?? false,
        hasHigherVersion: extended.hasHigherVersion,
        editMode: extended.editMode,
        status: status
    };
    return headerDataInfo;
});

export const selectTemplateFolders = createSelector(selectAnalysisState, state => state?.templateFolders);

export const selectTemplateOptions = createSelector(selectTemplateFolders, selectUserLanguage, (folders, language) => {
    let folderOptions: SelectListOption[] = [{ label: 'Own template', id: 'OWN' }];
    folders?.forEach(folder => {
        folderOptions = [
            ...folderOptions,
            {
                label: TranslationUtils.translateTextValue(folder.name, language)!,
                id: folder.id.toString()
            }
        ];
    });
    return folderOptions;
});

export const selectLoadingTemplateFolders = createSelector(selectAnalysisState, state => state?.loadingTemplateFolders);

export const selectPlantMuSchG = createSelector(selectMasterDataPlant, plant => plant?.muSchG ?? false);

export const selectIsTemplateAnalysis = createSelector(selectAnalysis, analysis => !!analysis?.template);

export const selectCanMarkAsAnnuallyReviewed = createSelector(
    selectStatus,
    selectIsNotEditMode,
    (status, isNotEditMode) => status === AnalysisStatus.VALID && isNotEditMode
);

export const selectReviewedOnDate = createSelector(selectAnalysis, analysis => analysis?.reviewedOnDate);

export const selectWorkedOnDate = createSelector(selectAnalysis, analysis => analysis?.workedOnDate);

export const selectResponsible = createSelector(selectAnalysis, analysis => analysis?.responsible);

export const selectWorkedOnBy = createSelector(selectAnalysis, analysis => analysis?.workedOnBy);

export const selectReviewedBy = createSelector(selectAnalysis, analysis => analysis?.reviewedBy);

export const selectIsMaxVersion = createSelector(selectVersion, selectMaxVersion, (version, maxVersion) => version === maxVersion);

export const selectAnnualReviewBasicConditions = createSelector(
    selectStatus,
    selectFormOfAnalysis,
    selectIsMaxVersion,
    selectTypeOfAnalysis,
    selectIsNotEditMode,
    selectIsTemplateAnalysis,
    selectIsPreview,
    (status, analysisForm, isMaxVersion, analysisType, isNotEditMode, isTemplateAnalysis, isPreview) => {
        if (!isNotEditMode) {
            return false;
        }

        if (isTemplateAnalysis || isPreview) {
            return false;
        }

        if (status !== AnalysisStatus.VALID) {
            return false;
        }

        if (analysisForm !== AnalysisForm.FULL) {
            return false;
        }

        if (!isMaxVersion) {
            return false;
        }

        return analysisType !== ('AVG' as AnalysisType) && analysisType !== ('EMA' as AnalysisType);
    }
);

export const selectCreatedDate = createSelector(selectAnalysis, analysis => analysis?.createdDate);
export const selectShowConfirmAnnualReviewButton = createSelector(
    selectAnnualReviewBasicConditions,
    selectValidFrom,
    selectReviewedOnDate,
    selectWorkedOnDate,
    selectCreatedDate,
    selectResponsible,
    selectCreatedBy,
    selectWorkedOnBy,
    selectReviewedBy,
    selectCurrentUserQNumber,
    (basicConditions, validFrom, reviewedOnDate, workedOnDate, createdDate, responsible, createdBy, workedOnBy, reviewedBy, currentUserQNumber) => {
        if (!basicConditions || !validFrom) {
            return false;
        }

        const isUserAllowed =
            responsible?.qNumber === currentUserQNumber ||
            createdBy?.qNumber === currentUserQNumber ||
            workedOnBy?.qNumber === currentUserQNumber ||
            reviewedBy?.qNumber === currentUserQNumber;

        if (!currentUserQNumber || !isUserAllowed) {
            return false;
        }

        if (DateService.isDateInFuture(validFrom)) {
            return false;
        }

        const referenceDate = reviewedOnDate ?? workedOnDate ?? createdDate;
        if (referenceDate) {
            return DateService.daysSince(referenceDate) > 365;
        }
        return true;
    }
);

export const selectShowDialogOnGroupAnalysisSave = createSelector(selectAnalysisState, state => state.showDialogOnGroupAnalysisSave);

export const selectIsSingleAnalysis = createSelector(selectTypeOfAnalysis, analysisType => !AnalysisUtils.isGroupAnalysis(analysisType));

export const selectIsGdmsAnalysis = createSelector(selectAnalysis, analysis => analysis?.isGDMSAnalysis);

export const selectShowEditButtonsForSupplierAnalysis = createSelector(selectIsSupplier, selectSupplierStatus, (isSupplier, supplierStatus) => {
    return !(
        (isSupplier && supplierStatus === AnalysisSupplierStatus.OUTSTANDING) ||
        (!isSupplier && supplierStatus === AnalysisSupplierStatus.NEW) ||
        (isSupplier && supplierStatus === AnalysisSupplierStatus.CONFIRMED)
    );
});

export const selectFirstCriterionToShow = createSelector(selectTypeOfAnalysis, analysisType => {
    if (AnalysisType.SUPPORT == AnalysisType[analysisType?.toString() as keyof typeof AnalysisType]) {
        return AnalysisPage.MM13;
    } else {
        return AnalysisUtils.isSupport(analysisType) ? AnalysisPage.MM08 : AnalysisPage.MM01;
    }
});

export const selectCriterionBarItems = createSelector(
    selectFirstCriterionToShow,
    selectAnalysisSupportMM13CriterionBar,
    selectAnalysisCriterionData,
    selectAnalysisSupportCriterionBar,
    selectPreviewAnalysisCriterionData,
    selectIsPreview,
    (firstCriterionToShow, supportMM13CriterionBar, analysisCriterionData, supportCriterionBar, previewAnalysisCriterionData, isPreview) => {
        if (isPreview) {
            return previewAnalysisCriterionData;
        } else if (firstCriterionToShow === AnalysisPage.MM13) {
            return supportMM13CriterionBar;
        } else if (firstCriterionToShow === AnalysisPage.MM08) {
            return supportCriterionBar;
        } else {
            return analysisCriterionData;
        }
    }
);

export const selectGDMSProcessData = createSelector(selectAnalysisState, state => state.analysis?.gdmsProcessData);

export const selectShowAnalysisActionButtons = createSelector(
    selectUserPermissions,
    selectIsNotEditMode,
    selectIsTemplateAnalysis,
    selectTypeOfAnalysis,
    selectSupplierStatus,
    selectShowEditButtonsForSupplierAnalysis,
    selectIsPreview,
    selectGDMSProcessData,
    selectStatus,
    (
        userPermissions,
        isNotEditMode,
        isTemplateAnalysis,
        typeOfAnalysis,
        supplierStatus,
        showEditButtonsForSupplierAnalysis,
        isPreview,
        gdmsProcessData,
        analysisStatus
    ) => {
        return (
            userPermissions?.includes('WRITE_ANALYSIS') &&
            isNotEditMode &&
            !isTemplateAnalysis &&
            AnalysisUtils.isEditableTypeAnalysis(typeOfAnalysis) &&
            supplierStatus !== AnalysisSupplierStatus.OUTSTANDING &&
            showEditButtonsForSupplierAnalysis &&
            !isPreview &&
            !GDMSUtils.isAnalysisFrozenByGDMS(gdmsProcessData, analysisStatus)
        );
    }
);

export const selectShowFooterBarOverviewMenu = createSelector(selectAnalysisState, state => state.showFooterBarOverviewMenu);

export const selectIsUserCreatorAndSupplierStatusOutstanding = createSelector(
    selectUserPrefs,
    selectCreatedBy,
    selectSupplierStatus,
    (user, createdBy, supplierStatus) => user?.qNumber === createdBy?.qNumber && supplierStatus === AnalysisSupplierStatus.OUTSTANDING
);

export const selectPPP = createSelector(selectAnalysis, analysis => analysis?.ppp);

export const selectPPP2 = createSelector(selectAnalysis, analysis => analysis?.ppp2);

export const selectCanConfirmOrDeclineSupplierAnalysis = createSelector(
    selectSupplierStatus,
    selectPPP,
    selectPPP2,
    selectCurrentUserQNumber,
    selectIsSupplier,
    (supplierStatus, PPP, PPP2, currentUserQNumber, isSupplier) => {
        return (
            supplierStatus == AnalysisSupplierStatus.OUTSTANDING && (PPP?.qNumber === currentUserQNumber || PPP2?.qNumber === currentUserQNumber) && !isSupplier
        );
    }
);

export const selectShowSupplierHandover = createSelector(
    selectIsSupplier,
    selectSupplierStatus,
    selectCreatedBy,
    selectCurrentUserQNumber,
    (isSupplier, supplierStatus, createdByUser, currentUserQNumber) => {
        return isSupplier && supplierStatus === AnalysisSupplierStatus.NEW && createdByUser?.qNumber === currentUserQNumber;
    }
);

export const selectShowSupplierMenu = createSelector(
    selectIsUserCreatorAndSupplierStatusOutstanding,
    selectCanConfirmOrDeclineSupplierAnalysis,
    (selectIsUserCreatorAndSupplierStatusOutstanding, selectCanConfirmOrDeclineSupplierAnalysis) => {
        return selectIsUserCreatorAndSupplierStatusOutstanding || selectCanConfirmOrDeclineSupplierAnalysis;
    }
);

export const selectMM01SliderBarColorLimits = createSelector(selectActiveConfigVersion, state => {
    return {
        yellowLimit: state?.defMM01?.staticStressYellowLimit,
        redLimit: state?.defMM01?.staticStressRedLimit
    };
});

export const selectMM06AllColorLimits = createSelector(selectActiveConfigVersion, state => {
    return {
        standingColorLimits: {
            standingGreenLimit: state?.defMM06?.standingGreenLimit,
            standingYellowLimit: state?.defMM06?.standingYellowLimit
        },
        standingExceptionalStressColorLimits: {
            standingExceptionalStressYellowLimit: state?.defMM06?.standingExceptionalStressYellowLimit,
            standingExceptionalStressRedLimit: state?.defMM06?.standingExceptionalStressRedLimit
        },
        sittingColorLimits: {
            sittingGreenLimit: state?.defMM06?.sittingGreenLimit,
            sittingYellowLimit: state?.defMM06?.sittingYellowLimit
        },
        sittingExceptionalStressColorLimits: {
            sittingExceptionalStressYellowLimit: state?.defMM06?.sittingExceptionalStressYellowLimit,
            sittingExceptionalStressRedLimit: state?.defMM06?.sittingExceptionalStressRedLimit
        },
        walkingColorLimits: {
            walkingGreenLimit: state?.defMM06?.walkingGreenLimit,
            walkingYellowLimit: state?.defMM06?.walkingYellowLimit
        },
        walkingExceptionalStressColorLimits: {
            walkingExceptionalStressYellowLimit: state?.defMM06?.walkingExceptionalStressYellowLimit,
            walkingExceptionalStressRedLimit: state?.defMM06?.walkingExceptionalStressRedLimit
        },
        sittingExceptionalNeelingColorLimits: {
            sittingExceptionalNeelingYellowLimit: state?.defMM06?.sittingExceptionalNeelingYellowLimit,
            sittingExceptionalNeelingRedLimit: state?.defMM06?.sittingExceptionalNeelingRedLimit
        }
    };
});

export const selectMM02StaticDynamicColorLimits = createSelector(selectActiveConfigVersion, state => {
    return {
        dynamicLevel1RedLimit: state?.defMM02?.dynamicLevel1RedLimit,
        dynamicLevel1YellowLimit: state?.defMM02?.dynamicLevel1YellowLimit,
        dynamicLevel2RedLimit: state?.defMM02?.dynamicLevel2RedLimit,
        dynamicLevel2YellowLimit: state?.defMM02?.dynamicLevel2YellowLimit,
        staticLevel1RedLimit: state?.defMM02?.staticLevel1RedLimit,
        staticLevel1YellowLimit: state?.defMM02?.staticLevel1YellowLimit,
        staticLevel2RedLimit: state?.defMM02?.staticLevel2RedLimit,
        staticLevel2YellowLimit: state?.defMM02?.staticLevel2YellowLimit
    };
});

export const selectGroupAnalysisElements = createSelector(selectAnalysis, state => state?.groupAnalysis?.groupAnalysisElements ?? []);

export const selectTvgsUsedForCalculation = createSelector(selectGroupAnalysisElements, elements => elements.filter(element => element.partOfCalculation));

export const selectTvgsNotUsedForCalculation = createSelector(selectGroupAnalysisElements, elements => elements.filter(element => !element.partOfCalculation));

export const selectTvgsUsedForCalculationWithUserLanguage = createSelector(selectTvgsUsedForCalculation, selectUserLanguage, (tvgs, userLanguage) => ({
    tvgs,
    userLanguage
}));

export const selectTvgsNotUsedForCalculationWithUserLanguage = createSelector(selectTvgsNotUsedForCalculation, selectUserLanguage, (tvgs, userLanguage) => ({
    tvgs,
    userLanguage
}));

export const selectMM03DynMediumTrunkFlexionColorLimits = createSelector(selectActiveConfigVersion, state => {
    return {
        yellowLimit: state?.defMM03?.dynMediumTrunkFlexionYellowLimit,
        redLimit: state?.defMM03?.dynMediumTrunkFlexionRedLimit
    };
});

export const selectMM03StatMediumTrunkFlexionColorLimits = createSelector(selectActiveConfigVersion, state => {
    return {
        yellowLimit: state?.defMM03?.statMediumTrunkFlexionYellowLimit,
        redLimit: state?.defMM03?.statMediumTrunkFlexionRedLimit
    };
});

export const selectMM03DynStrongTrunkFlexionColorLimits = createSelector(selectActiveConfigVersion, state => {
    return {
        yellowLimit: state?.defMM03?.dynStrongTrunkFlexionYellowLimit,
        redLimit: state?.defMM03?.dynStrongTrunkFlexionRedLimit
    };
});

export const selectMM03StatStrongTrunkFlexionColorLimits = createSelector(selectActiveConfigVersion, state => {
    return {
        yellowLimit: state?.defMM03?.statStrongTrunkFlexionYellowLimit,
        redLimit: state?.defMM03?.statStrongTrunkFlexionRedLimit
    };
});

export const selectMM03DynMediumTrunkRotationColorLimits = createSelector(selectActiveConfigVersion, state => {
    return {
        yellowLimit: state?.defMM03?.dynMediumTrunkRotationYellowLimit,
        redLimit: state?.defMM03?.dynMediumTrunkRotationRedLimit
    };
});

export const selectMM03StatMediumTrunkRotationColorLimits = createSelector(selectActiveConfigVersion, state => {
    return {
        yellowLimit: state?.defMM03?.statMediumTrunkRotationYellowLimit,
        redLimit: state?.defMM03?.statMediumTrunkRotationRedLimit
    };
});

export const selectMM03DynStrongLatTrunkFlexionColorLimits = createSelector(selectActiveConfigVersion, state => {
    return {
        yellowLimit: state?.defMM03?.dynStrongLatTrunkFlexionYellowLimit,
        redLimit: state?.defMM03?.dynStrongLatTrunkFlexionRedLimit
    };
});

export const selectMM03StatStrongLatTrunkFlexionColorLimits = createSelector(selectActiveConfigVersion, state => {
    return {
        yellowLimit: state?.defMM03?.statStrongLatTrunkFlexionYellowLimit,
        redLimit: state?.defMM03?.statStrongLatTrunkFlexionRedLimit
    };
});

export const selectMM03ExtensionColorLimits = createSelector(selectActiveConfigVersion, state => {
    return {
        yellowLimit: state?.defMM03?.extensionYellowLimit,
        redLimit: state?.defMM03?.extensionRedLimit
    };
});

export const selectHasDataMM13ItemsOrSupportItems = createSelector(
    selectAnalysisMM13,
    state => (state?.items?.length ?? 0) > 0 || (state?.itemsSupport?.length ?? 0) > 0
);

export const selectShowOperatingInstructions = createSelector(selectAnalysisState, state => state.showOperatingInstructions);

export const selectPendingOpenOperatingInstructions = createSelector(selectAnalysisState, state => state.pendingOpenOperatingInstructions);

export const selectTrainingValidation = createSelector(selectAnalysisState, state => state?.setTrainingValidation);

const selectCriterionColorsGroup1 = createSelector(
    selectAnalysisMM01Color,
    selectAnalysisMM02Color,
    selectAnalysisMM03Color,
    selectAnalysisMM04Color,
    selectAnalysisMM05Color,
    selectAnalysisMM06Color,
    selectAnalysisMM07Color,
    (mm01, mm02, mm03, mm04, mm05, mm06, mm07) => [mm01, mm02, mm03, mm04, mm05, mm06, mm07]
);

const selectCriterionColorsGroup2 = createSelector(
    selectAnalysisMM08Color,
    selectAnalysisMM09Color,
    selectAnalysisMM10Color,
    selectAnalysisMM11Color,
    selectAnalysisMM12Color,
    selectAnalysisMM13Color,
    (mm08, mm09, mm10, mm11, mm12, mm13) => [mm08, mm09, mm10, mm11, mm12, mm13]
);

export const selectAnalysisCriterionColors = createSelector(selectCriterionColorsGroup1, selectCriterionColorsGroup2, (group1, group2) => [
    ...group1,
    ...group2
]);

export const selectAnalysisTypesOptions = createSelector(
    selectAnalysisState,
    state =>
        Object.values(state?.analysisTypes ?? []).map(value => ({
            id: value,
            label: 'ENUM.ANALYIS_TYPE.' + value
        })) as SelectListOption[]
);

const SUPPORT_ANALYSIS_TYPES = new Set(['FREE_SUPPORT', 'SUPPORT_MUA', 'SUPPORT_TMO', 'SUPPORT']);

export const selectSupportAnalysisTypes = createSelector(
    selectAnalysisState,
    state =>
        Object.values(state?.analysisTypes ?? [])
            .filter(value => SUPPORT_ANALYSIS_TYPES.has(value.toString()))
            .map(value => ({
                id: value,
                label: 'ENUM.ANALYIS_TYPE.' + value
            })) as SelectListOption[]
);

export const selectNonSupportAnalysisTypes = createSelector(
    selectAnalysisState,
    state =>
        Object.values(state?.analysisTypes ?? [])
            .filter(value => !SUPPORT_ANALYSIS_TYPES.has(value.toString()))
            .map(value => ({
                id: value,
                label: 'ENUM.ANALYIS_TYPE.' + value
            })) as SelectListOption[]
);

export const selectAnalysisFormsOptions = createSelector(
    selectAnalysisState,
    state =>
        Object.values(state?.analysisForms ?? []).map(value => ({
            id: value,
            label: 'ENUM.ANALYIS_FORM.' + value
        })) as SelectListOption[]
);

export const selectHeaderDataIdentification = createSelector(
    selectAnalysis,
    selectUserLanguage,
    selectIsTVG,
    (analysis, userLanguage, isTVG): Partial<Analysis> => {
        return {
            analysisKey: analysis?.analysisKey,
            version: analysis?.version,
            createdBy: analysis?.createdBy,
            createdDate: analysis?.createdDate,
            workedOnBy: analysis?.workedOnBy,
            workedOnDate: analysis?.workedOnDate,
            reviewedBy: analysis?.reviewedBy,
            workPlaceNumber: analysis?.workPlaceNumber,
            plantCode: analysis?.plantCode,
            description: isTVG ? TranslationUtils.getI18nText(analysis?.i18nTextList, 'TVG_TITLE', userLanguage, analysis?.description) : analysis?.description,
            technologyCode: analysis?.technologyCode,
            analysisType: analysis?.analysisType,
            plantCCDC: analysis?.plantCCDC,
            analysisForm: analysis?.analysisForm,
            numberIdenticalWorkPlace: analysis?.numberIdenticalWorkPlace,
            descriptionLong: isTVG
                ? TranslationUtils.getI18nText(analysis?.i18nTextList, 'TVG_DESCRIPTION', userLanguage, analysis?.descriptionLong)
                : analysis?.descriptionLong,
            status: analysis?.status,
            validFrom: analysis?.validFrom,
            validTo: analysis?.validTo,
            timeUnitSecond: analysis?.timeUnitSecond,
            taskLength: analysis?.taskLength,
            processFrequency: analysis?.processFrequency,
            isRoutineActivity: analysis?.isRoutineActivity,
            pipRelevant: analysis?.pipRelevant,
            responsible: analysis?.responsible,
            ppp: analysis?.ppp,
            ppp2: analysis?.ppp2,
            singleAnalysis: {
                constantTime: analysis?.singleAnalysis?.constantTime,
                shortFormTransitionDate: analysis?.singleAnalysis?.shortFormTransitionDate
            },
            reviewedOnDate: analysis?.reviewedOnDate,
            template: analysis?.template
        } as Partial<Analysis>;
    }
);

export const selectUsers = createSelector(selectAnalysisState, state => state?.users);

export const selectSearchingUsers = createSelector(selectAnalysisState, state => state?.loadingSearchUsersTable);

export const selectAnalysisPlantName = createSelector(selectAnalysis, analysis => analysis?.plantName);

export const selectGroupAnalysis = createSelector(selectAnalysis, state => state?.groupAnalysis);

export const selectCompletenessFactor = createSelector(selectGroupAnalysis, groupAnalysis => groupAnalysis?.calculationFactor);

export const selectUtilization = createSelector(selectGroupAnalysis, groupAnalysis => groupAnalysis?.utilization);

export const selectConnectedGroups = createSelector(selectAnalysis, analysis => analysis?.groupList);

export const selectSupportAnalysis = createSelector(selectAnalysis, state => state?.supportAnalysis);

export const selectIsMuaAnalysis = createSelector(selectTypeOfAnalysis, analysisType => analysisType === ('MUA' as AnalysisType));

export const selectIsMuaSupport = createSelector(selectTypeOfAnalysis, analysisType => analysisType === ('SUPPORT_MUA' as AnalysisType));

export const selectHeaderDataOptional = createSelector(selectAnalysis, (analysis): Partial<Analysis> => {
    return {
        analysisKey: analysis?.analysisKey,
        plantCode: analysis?.plantCode,
        buildingCode: analysis?.buildingCode,
        tvgWorkplaces: analysis?.tvgWorkplaces,
        floorCode: analysis?.floorCode,
        sectionAreaCode: analysis?.sectionAreaCode,
        assemblyLineCode: analysis?.assemblyLineCode,
        cycleCode: analysis?.cycleCode,
        axis: analysis?.axis,
        furtherCreator: analysis?.furtherCreator,
        tvgAnalysis: analysis?.tvgAnalysis,
        tvgStatus: analysis?.tvgAnalysis?.tvgStatus,
        productLineCodes: analysis?.productLineCodes,
        derivativeCodes: analysis?.derivativeCodes,
        serialNumber: analysis?.serialNumber,
        moduleCode: analysis?.moduleCode,
        constructionGroup: analysis?.constructionGroup,
        machineCode: analysis?.machineCode,
        mmeTypes: analysis?.mmeTypes,
        timeAnalysisType: analysis?.tvgAnalysis?.timeAnalysisType,
        inventoryNumber: analysis?.inventoryNumber,
        position: analysis?.position,
        workDeck: analysis?.workDeck,
        taskGroup: analysis?.taskGroup,
        checkedBy: analysis?.checkedBy,
        checkedOn: analysis?.checkedOn,
        releasedBy: analysis?.releasedBy,
        releasedOn: analysis?.releasedOn
    } as Partial<Analysis>;
});

export const selectTvgWorkplaces = createSelector(selectAnalysis, analysis => analysis?.tvgWorkplaces ?? []);

export const selectMmeTypes = createSelector(selectActiveConfigVersion, state => state?.defMMETypes ?? []);

export const selectMmeTypesDropdownOptions = createSelector(selectMmeTypes, mmeTypes => {
    return mmeTypes?.map(mmeType => {
        return {
            id: mmeType.id.toString(),
            label: mmeType.mmeTypeCode.toString()
        } as SelectListOption;
    });
});

export const selectGDMSData = createSelector(selectAnalysisState, state => state.gdmsData);

export const selectGDMSUsers = createSelector(selectGDMSData, state => state?.gdmsUsers);

export const selectGDMSDepartments = createSelector(selectGDMSData, state => state?.gdmsDepartments);

export const selectIsGDMSModalLoading = createSelector(selectAnalysisState, state => state.loadingGDMSModal);

export const selectGDMSDocumentUrl = createSelector(selectAnalysisState, state => state?.gdmsDocumentUrl);

export const selectConnectedSingleAnalysis = createSelector(selectAnalysis, analysis => analysis?.connectedSingleAnalysis);

export const selectMaternityProtectionPregnancy = createSelector(selectAnalysis, analysis => analysis?.maternityProtectionPregnancy);

export const selectMaternityProtectionStill = createSelector(selectAnalysis, analysis => analysis?.maternityProtectionStill);

export const selectMaternityAutomationRecalcDisabled = createSelector(
    selectTypeOfAnalysis,
    selectIsNotEditMode,
    (type, readMode) =>
        readMode ||
        type === ('AVERAGE' as AnalysisType) ||
        type === ('ADDITIONAL' as AnalysisType) ||
        type === ('LEMO' as AnalysisType) ||
        type === ('MUA' as AnalysisType) ||
        type === ('DERIVATIVE_GROUP' as AnalysisType)
);

export const selectMaternityStillAutocalc = createSelector(selectAnalysis, analysis => analysis?.maternityStillAutocalc);

export const selectMaternityPregnancyAutocalc = createSelector(selectAnalysis, analysis => analysis?.maternityPregnancyAutocalc);

export const selectMaternityStill = createSelector(selectAnalysis, analysis => analysis?.maternityStill);

export const selectMaternityPregnancy = createSelector(selectAnalysis, analysis => analysis?.maternityPregnancy);

export const selectManualReasonForPregnancy = createSelector(selectAnalysis, analysis => analysis?.manualReasonForPregnancy);

export const selectManualReasonForStill = createSelector(selectAnalysis, analysis => analysis?.manualReasonForStill);

export const selectEditedByForPregnancy = createSelector(selectAnalysis, analysis => analysis?.editedByForPregnancy);

export const selectEditedOnDateForPregnancy = createSelector(selectAnalysis, analysis => analysis?.editedOnDateForPregnancy);

export const selectEditedByForStill = createSelector(selectAnalysis, analysis => analysis?.editedByForStill);

export const selectEditedOnDateForStill = createSelector(selectAnalysis, analysis => analysis?.editedOnDateForStill);

export const selectShowMaternityData = createSelector(
    selectMasterDataPlant,
    selectFormOfAnalysis,
    selectIsTemplateAnalysis,
    (plant, analysisForm, isTemplate) => {
        return (
            plant != null && (plant.plantCode === '09.20' || plant.plantCode === '09.30' || plant.muSchG) && analysisForm !== AnalysisForm.ITO && !isTemplate
        );
    }
);

export const selectEditModeWithAnalysisData = createSelector(
    selectIsEditMode,
    selectTypeOfAnalysis,
    selectFormOfAnalysis,
    selectValidFrom,
    selectValidTo,
    (isEditMode, analysisType, analysisForm, validFrom, validTo) => ({
        isEditMode,
        analysisType,
        analysisForm,
        validFrom,
        validTo
    })
);

export const selectGroupAnalysisTaskLengthManualOverride = createSelector(selectAnalysis, state => state?.groupAnalysis?.taskLengthManualOverride ?? false);

export const selectLockState = createSelector(selectAnalysisState, state => state.lockState);

export const selectLockData = createSelector(selectLockState, lockState => lockState.lockData);

export const selectIsAnalysisLocked = createSelector(selectLockData, lockData => lockData?.locked);

export const selectUserLockedOut = createSelector(selectLockState, lockState => lockState.userLockedOut);

export const selectIsLoadingLockData = createSelector(selectLockState, lockState => lockState.loadingLockData);

export const selectLockCCDCList = createSelector(selectLockState, lockState => lockState.lockCCDCList);

export const selectLoadingLockCCDCList = createSelector(selectLockState, lockState => lockState.loadingLockCCDCList);

export const selectLockCCDCListDropdownOptions = createSelector(selectLockCCDCList, ccdcs =>
    ccdcs?.map(ccdc => ({ id: ccdc.id.toString(), label: ccdc.displayName }))
);

export const selectShowLockControlPopup = createSelector(selectLockState, lockState => lockState.showLockControlPopup);

export const selectQRCodePdfUrl = createSelector(selectAnalysisState, state => state.qrCodePdfUrl);

export const selectLoadingPlannedTvgTime = createSelector(selectAnalysisState, state => state?.loadingPlannedTvgTimeReport);

export const selectPlannedTvgTime = createSelector(selectAnalysisState, state => state?.plannedTvgTimeReport);

export const selectShowLeaveAnalysisConfirmation = createSelector(selectAnalysisState, state => !!state?.showLeaveAnalysisConfirmation);
